import { useState, useEffect } from 'react';
import { IoAdd, IoTrash, IoEye, IoStatsChart, IoSend, IoCalendar, IoTime } from 'react-icons/io5';
import { notificationApi } from '../../services/api';
import LoadingSpinner from '../LoadingSpinner';
import './NotificationManager.css';

const NotificationManager = () => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showStats, setShowStats] = useState(false);
  const [stats, setStats] = useState(null);
  const [filters, setFilters] = useState({
    type: '',
    category: '',
    targetType: '',
    isActive: ''
  });

  const [formData, setFormData] = useState({
    title: '',
    message: '',
    type: 'info',
    priority: 'normal',
    category: 'general',
    targetType: 'all',
    targetIds: [],
    metadata: {},
    scheduledAt: '',
    expiresAt: ''
  });

  useEffect(() => {
    loadNotifications();
  }, [currentPage, filters]);

  const loadNotifications = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Only include non-empty filter values
      const cleanFilters = Object.fromEntries(
        Object.entries(filters).filter(([, value]) => 
          value !== null && value !== undefined && value !== ''
        )
      );
      
      const params = {
        page: currentPage,
        limit: 10,
        ...cleanFilters
      };
      
      const response = await notificationApi.getAllNotifications(params);
      setNotifications(response.data.notifications || []);
      setTotalPages(response.data.pagination?.pages || 1);
    } catch (err) {
      console.error('Failed to load notifications:', err);
      setError('Failed to load notifications');
    } finally {
      setLoading(false);
    }
  };

  const loadNotificationStats = async (notificationId) => {
    try {
      const response = await notificationApi.getNotificationStats(notificationId);
      setStats(response.data.stats);
      setShowStats(true);
    } catch (err) {
      console.error('Failed to load notification stats:', err);
      setError('Failed to load notification statistics');
    }
  };

  const handleCreateNotification = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      
      // Clean up the form data before sending
      const cleanFormData = { ...formData };
      
      // Remove empty targetIds if targetType is 'all'
      if (cleanFormData.targetType === 'all') {
        delete cleanFormData.targetIds;
      }
      
      // Remove empty metadata if it's empty
      if (!cleanFormData.metadata || Object.keys(cleanFormData.metadata).length === 0) {
        delete cleanFormData.metadata;
      }
      
      // Remove empty scheduledAt and expiresAt
      if (!cleanFormData.scheduledAt) {
        delete cleanFormData.scheduledAt;
      }
      if (!cleanFormData.expiresAt) {
        delete cleanFormData.expiresAt;
      }
      
      await notificationApi.createNotification(cleanFormData);
      setShowCreateForm(false);
      setFormData({
        title: '',
        message: '',
        type: 'info',
        priority: 'normal',
        category: 'general',
        targetType: 'all',
        targetIds: [],
        metadata: {},
        scheduledAt: '',
        expiresAt: ''
      });
      loadNotifications();
    } catch (err) {
      console.error('Failed to create notification:', err);
      
      // Show specific validation errors if available
      if (err.response?.data?.errors) {
        const errorMessages = err.response.data.errors.map(error => error.msg).join(', ');
        setError(`Validation errors: ${errorMessages}`);
      } else if (err.response?.data?.error) {
        setError(err.response.data.error);
      } else {
        setError('Failed to create notification');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteNotification = async (notificationId) => {
    if (!window.confirm('Are you sure you want to delete this notification?')) {
      return;
    }

    try {
      await notificationApi.deleteNotification(notificationId);
      loadNotifications();
    } catch (err) {
      console.error('Failed to delete notification:', err);
      setError('Failed to delete notification');
    }
  };

  const handleSendScheduledNotification = async (notificationId) => {
    try {
      await notificationApi.sendScheduledNotification(notificationId);
      loadNotifications();
    } catch (err) {
      console.error('Failed to send scheduled notification:', err);
      setError('Failed to send scheduled notification');
    }
  };

  const getNotificationIcon = (type) => {
    const icons = {
      info: 'ℹ️',
      success: '✅',
      warning: '⚠️',
      error: '❌',
      announcement: '📢',
      reminder: '⏰',
      achievement: '🏆'
    };
    return icons[type] || '📢';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  const isScheduled = (notification) => {
    return notification.scheduledAt && new Date(notification.scheduledAt) > new Date();
  };

  const isExpired = (notification) => {
    return notification.expiresAt && new Date(notification.expiresAt) < new Date();
  };

  return (
    <div className="notification-manager">
      <div className="notification-manager-header">
        <h2>Notification Management</h2>
        <button 
          className="create-notification-btn"
          onClick={() => setShowCreateForm(true)}
        >
          <IoAdd size={16} />
          Create Notification
        </button>
      </div>

      {error && (
        <div className="error-message">
          {error}
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}

      {/* Filters */}
      <div className="notification-filters">
        <select 
          value={filters.type} 
          onChange={(e) => setFilters(prev => ({ ...prev, type: e.target.value }))}
        >
          <option value="">All Types</option>
          <option value="info">Info</option>
          <option value="success">Success</option>
          <option value="warning">Warning</option>
          <option value="error">Error</option>
          <option value="announcement">Announcement</option>
          <option value="reminder">Reminder</option>
          <option value="achievement">Achievement</option>
        </select>

        <select 
          value={filters.category} 
          onChange={(e) => setFilters(prev => ({ ...prev, category: e.target.value }))}
        >
          <option value="">All Categories</option>
          <option value="system">System</option>
          <option value="reading">Reading</option>
          <option value="family">Family</option>
          <option value="church">Church</option>
          <option value="gamification">Gamification</option>
          <option value="admin">Admin</option>
          <option value="general">General</option>
        </select>

        <select 
          value={filters.targetType} 
          onChange={(e) => setFilters(prev => ({ ...prev, targetType: e.target.value }))}
        >
          <option value="">All Target Types</option>
          <option value="all">All Users</option>
          <option value="users">Specific Users</option>
          <option value="readers">Readers</option>
          <option value="families">Families</option>
          <option value="churches">Churches</option>
          <option value="specific">Custom</option>
        </select>

        <select 
          value={filters.isActive} 
          onChange={(e) => setFilters(prev => ({ ...prev, isActive: e.target.value }))}
        >
          <option value="">All Status</option>
          <option value="true">Active</option>
          <option value="false">Inactive</option>
        </select>
      </div>

      {/* Notifications List */}
      <div className="notifications-list">
        {loading ? (
          <LoadingSpinner />
        ) : notifications.length === 0 ? (
          <div className="empty-state">
            <p>No notifications found</p>
          </div>
        ) : (
          notifications.map((notification) => (
            <div key={notification.id} className="notification-card">
              <div className="notification-header">
                <div className="notification-icon">
                  {getNotificationIcon(notification.type)}
                </div>
                <div className="notification-info">
                  <h3>{notification.title}</h3>
                  <p>{notification.message}</p>
                  <div className="notification-meta">
                    <span className={`priority-badge priority-${notification.priority}`}>
                      {notification.priority}
                    </span>
                    <span className="category-badge">
                      {notification.category}
                    </span>
                    <span className="target-type">
                      {notification.targetType}
                    </span>
                    {isScheduled(notification) && (
                      <span className="scheduled-badge">
                        <IoCalendar size={12} />
                        Scheduled
                      </span>
                    )}
                    {isExpired(notification) && (
                      <span className="expired-badge">
                        <IoTime size={12} />
                        Expired
                      </span>
                    )}
                  </div>
                </div>
                <div className="notification-actions">
                  <button 
                    className="action-btn stats-btn"
                    onClick={() => loadNotificationStats(notification.id)}
                    title="View Statistics"
                  >
                    <IoStatsChart size={16} />
                  </button>
                  {isScheduled(notification) && (
                    <button 
                      className="action-btn send-btn"
                      onClick={() => handleSendScheduledNotification(notification.id)}
                      title="Send Now"
                    >
                      <IoSend size={16} />
                    </button>
                  )}
                  <button 
                    className="action-btn delete-btn"
                    onClick={() => handleDeleteNotification(notification.id)}
                    title="Delete"
                  >
                    <IoTrash size={16} />
                  </button>
                </div>
              </div>
              <div className="notification-details">
                <div className="detail-row">
                  <span>Created:</span>
                  <span>{formatDate(notification.createdAt)}</span>
                </div>
                {notification.scheduledAt && (
                  <div className="detail-row">
                    <span>Scheduled:</span>
                    <span>{formatDate(notification.scheduledAt)}</span>
                  </div>
                )}
                {notification.expiresAt && (
                  <div className="detail-row">
                    <span>Expires:</span>
                    <span>{formatDate(notification.expiresAt)}</span>
                  </div>
                )}
                {notification.sentAt && (
                  <div className="detail-row">
                    <span>Sent:</span>
                    <span>{formatDate(notification.sentAt)}</span>
                  </div>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="pagination">
          <button 
            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
            disabled={currentPage === 1}
          >
            Previous
          </button>
          <span>Page {currentPage} of {totalPages}</span>
          <button 
            onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
            disabled={currentPage === totalPages}
          >
            Next
          </button>
        </div>
      )}

      {/* Create Notification Modal */}
      {showCreateForm && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>Create New Notification</h3>
              <button onClick={() => setShowCreateForm(false)}>×</button>
            </div>
            <form onSubmit={handleCreateNotification}>
              <div className="form-group">
                <label>Title *</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                  required
                />
              </div>
              
              <div className="form-group">
                <label>Message *</label>
                <textarea
                  value={formData.message}
                  onChange={(e) => setFormData(prev => ({ ...prev, message: e.target.value }))}
                  required
                  rows={4}
                />
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label>Type</label>
                  <select
                    value={formData.type}
                    onChange={(e) => setFormData(prev => ({ ...prev, type: e.target.value }))}
                  >
                    <option value="info">Info</option>
                    <option value="success">Success</option>
                    <option value="warning">Warning</option>
                    <option value="error">Error</option>
                    <option value="announcement">Announcement</option>
                    <option value="reminder">Reminder</option>
                    <option value="achievement">Achievement</option>
                  </select>
                </div>
                
                <div className="form-group">
                  <label>Priority</label>
                  <select
                    value={formData.priority}
                    onChange={(e) => setFormData(prev => ({ ...prev, priority: e.target.value }))}
                  >
                    <option value="low">Low</option>
                    <option value="normal">Normal</option>
                    <option value="high">High</option>
                    <option value="urgent">Urgent</option>
                  </select>
                </div>
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label>Category</label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                  >
                    <option value="system">System</option>
                    <option value="reading">Reading</option>
                    <option value="family">Family</option>
                    <option value="church">Church</option>
                    <option value="gamification">Gamification</option>
                    <option value="admin">Admin</option>
                    <option value="general">General</option>
                  </select>
                </div>
                
                <div className="form-group">
                  <label>Target Type</label>
                  <select
                    value={formData.targetType}
                    onChange={(e) => setFormData(prev => ({ ...prev, targetType: e.target.value }))}
                  >
                    <option value="all">All Users</option>
                    <option value="users">Specific Users</option>
                    <option value="readers">Readers</option>
                    <option value="families">Families</option>
                    <option value="churches">Churches</option>
                    <option value="specific">Custom</option>
                  </select>
                </div>
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label>Schedule (Optional)</label>
                  <input
                    type="datetime-local"
                    value={formData.scheduledAt}
                    onChange={(e) => setFormData(prev => ({ ...prev, scheduledAt: e.target.value }))}
                  />
                </div>
                
                <div className="form-group">
                  <label>Expires (Optional)</label>
                  <input
                    type="datetime-local"
                    value={formData.expiresAt}
                    onChange={(e) => setFormData(prev => ({ ...prev, expiresAt: e.target.value }))}
                  />
                </div>
              </div>
              
              <div className="modal-actions">
                <button type="button" onClick={() => setShowCreateForm(false)}>
                  Cancel
                </button>
                <button type="submit" disabled={loading}>
                  {loading ? 'Creating...' : 'Create Notification'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Statistics Modal */}
      {showStats && stats && (
        <div className="modal-overlay">
          <div className="modal stats-modal">
            <div className="modal-header">
              <h3>Notification Statistics</h3>
              <button onClick={() => setShowStats(false)}>×</button>
            </div>
            <div className="stats-content">
              <div className="stats-grid">
                <div className="stat-card">
                  <h4>Total Recipients</h4>
                  <p className="stat-number">{stats.total}</p>
                </div>
                <div className="stat-card">
                  <h4>Read</h4>
                  <p className="stat-number">{stats.read}</p>
                  <p className="stat-percentage">{stats.readRate}%</p>
                </div>
                <div className="stat-card">
                  <h4>Delivered</h4>
                  <p className="stat-number">{stats.delivered}</p>
                  <p className="stat-percentage">{stats.deliveryRate}%</p>
                </div>
              </div>
              
              {stats.breakdown && stats.breakdown.length > 0 && (
                <div className="stats-breakdown">
                  <h4>Detailed Breakdown</h4>
                  <div className="breakdown-list">
                    {stats.breakdown.map((item, index) => (
                      <div key={index} className="breakdown-item">
                        <span>Status: {item.deliveryStatus}</span>
                        <span>Read: {item.isRead ? 'Yes' : 'No'}</span>
                        <span>Action: {item.userAction}</span>
                        <span>Count: {item.count}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationManager;
